/**
 *  [2023] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

/**
 * 組織単位で案件を管理するDBの使い方をサポートするキャッシュ機能
 *  caseBasedDataManagementを拡張する
 */

import {CacheManage, TMediaInfo, TRecordWithMedia} from '@zettant/cacheManage';
import {DB_VERSION} from './multiOrgCaseBasedDataManageInitializer';
import {
  ICaseBase,
  IOrganizationBase,
} from '@stacks/multiOrganizationCaseDataManagement/interfaces';
import pako from 'pako';
import {Base64} from 'js-base64';
import {ICaseListEntry} from '@stacks/caseBasedDataManagement';
import {forceDeleteCaseForTesting} from '@stacks/multiOrganizationCaseDataManagement/wrappers';
import {IErrorMessage} from '../../common/logger/errorMessages';

type TArchiveStat = 'inTable' | 'archiving' | 'archived' | 'willDelete' | 'deleted';

export const CASELIST_TAG = '++CASELIST';


class MultiOrgCaseDataManage extends CacheManage {
  private organizationId = '';  // 基本的に複数の組織に属することはないので、何らかの値にセットされたらそれ以降同じIDを用いるはず
  private adminMode = false;  // trueの場合、getByTime()などで、primaryKeyの先頭にorganizationIdがついているかのチェックを行わない

  public createCaseAdapter = async (record: ICaseBase): Promise<any> => {};
  public updateCaseAdapter = async (record: ICaseBase): Promise<any> => {};
  public deleteCaseAdapter = async (caseId: string): Promise<any> => {};
  public createOrganizationAdapter = async (record: IOrganizationBase): Promise<any> => {};
  public updateOrganizationAdapter = async (record: IOrganizationBase): Promise<any> => {};
  public deleteOrganizationAdapter = async (organizationId: string): Promise<any> => {};
  public readOrganizationsAdapter = async (): Promise<any> => {};
  public readInOrganizationAdapter = async (updatedAt: number, organizationId?: string): Promise<any> => {};
  public flushForTestingAdapter = async (): Promise<any> => {};
  public rawPutAdapter = async (records: any): Promise<any> => {};
  public archiveForTestingAdapter = async (): Promise<any> => {};
  public forceDeleteCaseForTestingAdapter = async (caseId: string): Promise<any> => {};

  public getDb = () => this.db;
  //public setTolerance = (val: number) => super.toleranceOfGetByTime = val;

  constructor(tableName: string, initializer: (tableName: string, initParams: any, version?: number)=>any, initParams: any, version=DB_VERSION) {
    super(tableName, 'caseId', 'dataId', initializer, initParams, version);
  }

  public setOrganizationId = (organizationId: string) => this.organizationId = organizationId;
  public setAdminMode = (flag=true) => this.adminMode = flag;

  /**
   * アダプターをセットする。ついでにgetBlockListAdapterがセットされた場合はブロックリストを取得する
   * @param createAdapter
   * @param updateAdapter
   * @param bulkCreateAdapter
   * @param bulkUpdateAdapter
   * @param deleteAdapter
   * @param readAdapter2
   * @param readAllAdapter
   * @param putMediaAdapter
   * @param bulkPutMediaAdapter
   * @param getMediaAdapter
   * @param deleteMediaAdapter
   * @param createCaseAdapter
   * @param updateCaseAdapter
   * @param deleteCaseAdapter
   * @param createOrganizationAdapter
   * @param updateOrganizationAdapter
   * @param deleteOrganizationAdapter
   * @param readOrganizationsAdapter
   * @param readInOrganizationAdapter
   * @param flushForTestingAdapter
   * @param forceDeleteCaseForTestingAdapter
   * @param rawPutAdapter
   * @param archiveForTestingAdapter
   */
  public setup({createAdapter, updateAdapter, bulkCreateAdapter, bulkUpdateAdapter, deleteAdapter, readAdapter2, readAllAdapter, putMediaAdapter, bulkPutMediaAdapter, getMediaAdapter, deleteMediaAdapter, createCaseAdapter, updateCaseAdapter, deleteCaseAdapter, createOrganizationAdapter, updateOrganizationAdapter, deleteOrganizationAdapter, readInOrganizationAdapter, readOrganizationsAdapter, putPrimaryArchiveAdapter, flushForTestingAdapter, forceDeleteCaseForTestingAdapter, rawPutAdapter, archiveForTestingAdapter}: any) {
    super.setup({createAdapter, updateAdapter, deleteAdapter,
      readAdapter2, readAllAdapter,
      putMediaAdapter, getMediaAdapter, deleteMediaAdapter,
      bulkCreateAdapter, bulkUpdateAdapter, bulkPutMediaAdapter,
      flushForTestingAdapter, forceDeleteCaseForTestingAdapter,
      archiveForTestingAdapter,
    });
    if (createCaseAdapter) this.createCaseAdapter = createCaseAdapter;
    if (updateCaseAdapter) this.updateCaseAdapter = updateCaseAdapter;
    if (deleteCaseAdapter) this.deleteCaseAdapter = deleteCaseAdapter;
    if (createOrganizationAdapter) this.createOrganizationAdapter = createOrganizationAdapter;
    if (updateOrganizationAdapter) this.updateOrganizationAdapter = updateOrganizationAdapter;
    if (deleteOrganizationAdapter) this.deleteOrganizationAdapter = deleteOrganizationAdapter;
    if (readOrganizationsAdapter) this.readOrganizationsAdapter = readOrganizationsAdapter;
    if (readInOrganizationAdapter) this.readInOrganizationAdapter = readInOrganizationAdapter;
    if (archiveForTestingAdapter) this.archiveForTestingAdapter = archiveForTestingAdapter;
    if (forceDeleteCaseForTestingAdapter) this.forceDeleteCaseForTestingAdapter = forceDeleteCaseForTestingAdapter;
    if (rawPutAdapter) this.rawPutAdapter = rawPutAdapter;
  }

  private getTagWithOrgId = () => `${CASELIST_TAG}##${this.organizationId}`;

  /**
   *
   * @param primaryKeyValue
   * @param secondaryKeyValue
   */
  public async get (primaryKeyValue: string, secondaryKeyValue: string): Promise<any> {
    // 組織IDが先頭についていない場合は強制的に付与する
    if (!this.adminMode && !primaryKeyValue.startsWith(CASELIST_TAG) && !primaryKeyValue.startsWith(`${this.organizationId}__`)) {
      primaryKeyValue = `${this.organizationId}__${primaryKeyValue}`;
    }
    return await super.get(primaryKeyValue, secondaryKeyValue);
  }

  /**
   * _getByTime() へのラッパー（archiveを削除する）
   *
   * @param primaryKeyValue {string}
   * @param timestamp {number}
   * @param withDeleted {boolean} trueにすると、deletedフラグのあるものも返答する
   */
  public getByTime = async (primaryKeyValue: string, timestamp=0, withDeleted=false): Promise<any[]> => {
    // 組織IDが先頭についていない場合は強制的に付与する
    if (!this.adminMode && !primaryKeyValue.startsWith(CASELIST_TAG) && !primaryKeyValue.startsWith(`${this.organizationId}__`)) {
      primaryKeyValue = `${this.organizationId}__${primaryKeyValue}`;
    }
    const res = await this._getByTime(primaryKeyValue, timestamp, withDeleted);
    if (!res) return res;
    return res.filter((ent: any) => !ent.dataId.endsWith('_archive'));
  };

  /**
   * 書き込み前に組織が登録済みかどうか、案件IDに組織IDが含まれているかをチェックして、案件情報を書き込む
   */
  public async put(value: any, media?: TMediaInfo, errorObj?: IErrorMessage) {
    if (!this.adminMode && !value.caseId.startsWith(CASELIST_TAG) && !value.caseId.startsWith(`${this.organizationId}__`)) {
      value.caseId = `${this.organizationId}__${value.caseId}`;
    }

    if (!value.caseId.startsWith('++') && !value.caseId.startsWith('--')) {
      // EXP: caseBasedでは、通常の（案件ではない）primaryKeyは受け付けない。例外は++や--がついているものだけ
      const primaryKey = this.adminMode ? `${CASELIST_TAG}##${value.caseId.split('__')[0]}` : this.getTagWithOrgId();
      const ent: any = await this.getByTime(primaryKey);
      if (!ent.some((c) => c.dataId === value.caseId.split('$$')[0])) {
        console.error('まずregisterNewCase()でcaseを登録する必要があります');
        return;
      }
    }

    return await super.put(value, media, errorObj);
  }

  /**
   * 書き込み前に、案件IDに組織IDが含まれているかをチェックして、案件情報を書き込む
   * @param records
   * @param errorObj
   */
  public async bulkPut(records: any[], errorObj?: IErrorMessage) {
    if (!this.adminMode) {
      for (const r of records) {
        if (!r.caseId.startsWith(CASELIST_TAG) && !r.caseId.startsWith(`${this.organizationId}__`)) {
          r.caseId = `${this.organizationId}__${r.caseId}`;
        }
      }
    }
    return await super.bulkPut(records, errorObj);
  }

  /**
   * 書き込み前に、案件IDに組織IDが含まれているかをチェックして、案件情報を書き込む
   * @param valueWithMedia {{record: any, media?: TMediaInfo}[]}
   * @param errorObj {IErrorMessage}
   */
  public async bulkPutWithMedia(valueWithMedia: TRecordWithMedia[], errorObj?:IErrorMessage) {
    if (!this.adminMode) {
      for (const r of valueWithMedia) {
        if (!r.record.caseId.startsWith(CASELIST_TAG) && !r.record.caseId.startsWith(`${this.organizationId}__`)) {
          r.record.caseId = `${this.organizationId}__${r.record.caseId}`;
        }
      }
    }
    return super.bulkPutWithMedia(valueWithMedia, errorObj);
  }

  /**
   * 書き込み前に、案件IDに組織IDが含まれているかをチェックして、案件情報を削除する
   */
  public async removeWithoutDelFlag(primaryKeyValue: string, secondaryKeyValue?: any, info={}): Promise<boolean> {
    if (!this.adminMode && !primaryKeyValue.startsWith(CASELIST_TAG) && !primaryKeyValue.startsWith(`${this.organizationId}__`)) {
      primaryKeyValue = `${this.organizationId}__${primaryKeyValue}`;
    }
    return super.removeWithoutDelFlag(primaryKeyValue, secondaryKeyValue, info);
  }

  /**
   * 書き込み前に、案件IDに組織IDが含まれているかをチェックして、案件情報を削除する
   */
  public async remove(primaryKeyValue: string, secondaryKeyValue?: any, info={}, errorObj?:IErrorMessage): Promise<boolean> {
    if (!this.adminMode && !primaryKeyValue.startsWith(CASELIST_TAG) && !primaryKeyValue.startsWith(`${this.organizationId}__`)) {
      primaryKeyValue = `${this.organizationId}__${primaryKeyValue}`;
    }
    return super.remove(primaryKeyValue, secondaryKeyValue, info, errorObj);
  }

  /**
   * 書き込み前に、案件IDに組織IDが含まれているかをチェックして、案件情報を削除する
   */
  public async bulkRemove(entries: any[], withoutDelFlag=false, errorObj?: IErrorMessage): Promise<any> {
    if (!this.adminMode) {
      for (const r of entries) {
        console.log('r:',r);
        if (!r.caseId.startsWith(CASELIST_TAG) && !r.caseId.startsWith(`${this.organizationId}__`)) {
          r.caseId = `${this.organizationId}__${r.caseId}`;
        }
      }
    }
    return super.bulkRemove(entries, withoutDelFlag, errorObj);
  }

  /**
   * 書き込み前に、案件IDに組織IDが含まれているかをチェックして、案件情報を削除する
   * @param primaryKeyValue {string}
   * @param secondaryKeyValue {string}
   */
  public async forceDelete(primaryKeyValue: string, secondaryKeyValue?: any): Promise<boolean> {
    if (!this.adminMode && !primaryKeyValue.startsWith(CASELIST_TAG) && !primaryKeyValue.startsWith(`${this.organizationId}__`)) {
      primaryKeyValue = `${this.organizationId}__${primaryKeyValue}`;
    }
    return super.forceDelete(primaryKeyValue, secondaryKeyValue);
  }

  /**
   * 全案件のリストを取得する
   * @protected
   * @param withDeleted {boolean} purgeの時だけtrueにする
   */
  public getCaseList = async (withDeleted=false): Promise<ICaseListEntry[]> => {
    await this.getByTime(this.getTagWithOrgId(), 0, withDeleted);
    const caseList = this.dataCache.getSortedArray(this.getTagWithOrgId());

    const results: ICaseListEntry[] = [];
    caseList.forEach((c: any) => {
      if (!withDeleted && c.deleted) return;
      if (c.dataId === '__base') return;
      const tmp: any = {};
      for (const x in c) {
        if (x === 'caseId') continue;
        if (x === 'dataId') {
          tmp.caseId = c.dataId;
        } else {
          tmp[x] = c[x];
        }
      }
      results.push(tmp);
    });

    return results;
  };


  /**
   * 指定した組織の全案件のリストを取得する
   * @protected
   * @param targetOrganizationId 対象組織のID
   * @param withDeleted {boolean} purgeの時だけtrueにする
   * @returns {Promise<ICaseListEntry[]>}
   */
  public getCaseListInGroup = async (targetOrganizationId: string, withDeleted=false): Promise<ICaseListEntry[]> => {
    const caseId = `${CASELIST_TAG}##${targetOrganizationId}`;
    await this.getByTime(caseId, 0, withDeleted);
    const caseList = this.dataCache.getSortedArray(caseId);

    const results: ICaseListEntry[] = [];
    caseList.forEach((c: any) => {
      if (!withDeleted && c.deleted) return;
      if (c.dataId === '__base') return;
      const tmp: any = {};
      for (const x in c) {
        if (x === 'caseId') continue;
        if (x === 'dataId') {
          tmp.caseId = c.dataId;
        } else {
          tmp[x] = c[x];
        }
      }
      results.push(tmp);
    });

    return results;
  };

  /**
   * CASELISTに新しい案件を作成する
   * @param caseId
   * @param info
   * @param organizationId {string}
   */
  public registerNewCase = async (caseId: string, info: any, organizationId?: string) => {
    // EXP: 本来は、caseId=CASELIST_TAG+organizationId、dataId=organizationId__案件IDだが、organizationIdはサーバ側で付与する
    const entry: ICaseBase = { caseId: CASELIST_TAG, dataId: caseId, info};
    if (this.adminMode) {
      if (!organizationId) {
        throw new Error('adminMode=trueでは、registerNewCasするときにorganizationIを指定してください');
      }
      entry.caseId = `${CASELIST_TAG}##${organizationId}`;
      entry.dataId = `${organizationId}__${caseId}`;
    }
    const updatedAt = await this.createCaseAdapter(entry);
    return !!updatedAt;
  };

  /**
   * 案件に完了時刻をセットする
   * @param caseId
   * @param timestamp
   */
  public completeCase = async (caseId: string, timestamp?: number) => {
    await this.getByTime(this.getTagWithOrgId(), this.timestampManage.getLatest(this.getTagWithOrgId()));
    const caseList = this.dataCache.getSortedArray(this.getTagWithOrgId());
    const ent = caseList.find((c: any) => c.dataId === caseId);
    if (!ent) return;
    if (typeof timestamp === 'undefined') {
      ent.completedAt = new Date().getTime();
    } else {
      ent.completedAt = 0;
    }
    ent.completedAt = timestamp || new Date().getTime();
    const updatedAt = await this.updateCaseAdapter(ent);
    if (updatedAt) await this.getByTime(caseId, updatedAt);
  };

  /**
   * 指定された案件に関する全ての情報を削除する
   * TODO: 他の人が「消えた」と気づく必要がある。ー＞定期的にpurgeすることにする
   * @param caseId
   */
  public deleteCase = async (caseId: string): Promise<boolean> => {
    // ローカルの情報も直ちに削除する
    if (!this.adminMode && !caseId.startsWith(CASELIST_TAG) && !caseId.startsWith(`${this.organizationId}__`)) {
      caseId = `${this.organizationId}__${caseId}`;
    }

    const res = await this.deleteCaseAdapter(caseId);
    // EXP: 案件削除が成功するとtrue、失敗するとfalseが帰ってくる
    if (!res) return false;
    await this.getByTime(this.getTagWithOrgId());
    await this.getByTime(caseId);

    await Promise.all([
      super.deleteLocal(this.getTagWithOrgId(), caseId),
      super.bulkDeleteLocal(caseId),
    ]);
    return true;
  };


  /**
   * 組織情報を読み込む
   * @param organizationId {string} システム管理者の場合はから文字以外も指定できる（ほぼデバッグ用）
   */
  public getOrganizationBaseInfo = async (organizationId= ''): Promise<IOrganizationBase> => {
    // TODO: Lambdaの場合は、システム管理者権限のある人だけorganizationを指定できる
    const tag = !organizationId ? this.getTagWithOrgId() : `${CASELIST_TAG}##${organizationId}`;
    const ent: any = await this.getByTime(tag);
    return ent.find((ent: any) => ent.dataId === '__base');
  };

  /**
   * 組織（＝組織基本情報）を登録する（システム管理権限を持つもののみ）
   * @param name {string} 組織名
   * @param note {string} その他情報
   * @param contract {string} 契約情報
   * @param organizationId
   */
  public createOrganization = async (name: string, note: string, contract: string, organizationId: string): Promise<boolean> => {
    const caseId = `${CASELIST_TAG}##${organizationId}`;
    const record: IOrganizationBase = {
      caseId,
      dataId: '__base',
      dataType: 'base',
      organization: name,
      contract,
      note,
    };
    const updatedAt = await this.createOrganizationAdapter(record);
    if (updatedAt) {
      await this.getByTime(caseId, updatedAt);
    }
    return !!updatedAt;
  };

  /**
   * 組織基本情報を更新する（システム管理権限を持つもののみ）
   * @param name {string} 組織名
   * @param note {string} その他情報
   * @param contract {string} 契約情報
   * @param organizationId {string} システム管理者以外は空文字。その場合はサーバがIDトークンのgroupから値を取得してその値をorganizationIdとする
   */
  public updateOrganization = async (name: string, note: string, contract: string, organizationId='') => {
    const caseId = `${CASELIST_TAG}##${organizationId}`;
    const record: IOrganizationBase = {
      caseId,
      dataId: '__base',
      dataType: 'base',
      organization: name,
      contract,
      note,
    };
    const updatedAt = await this.updateOrganizationAdapter(record);
    if (updatedAt) {
      await this.getByTime(caseId, updatedAt);
    }
    return !!updatedAt;
  };

  /**
   * 組織を削除する（システム管理権限を持つもののみ）
   * @param organizationId {string}
   */
  public deleteOrganization = async (organizationId) => {
    const res = await this.deleteOrganizationAdapter(organizationId);
    if (!res) return;
    await this.getOrganizationBaseInfo(organizationId);  // ローカルキャッシュを更新する（削除フラグがたったエントリを取得する）
  };

  /**
   * 組織一覧を読み込む（システム管理権限を持つもののみ）
   */
  public readAllOrganizations = async () => {
    const retDatB64: string = await this.readOrganizationsAdapter();
    if (!retDatB64) return [];
    return JSON.parse(pako.inflate(Base64.toUint8Array(retDatB64), {to: 'string'}));
  };

  /**
   * キャッシュから不要な、または古いデータを削除する
   */
  public async purge(lifetime=604800) {
    let cacheLifetime: any = this.db.meta.get('cacheLifetime');
    if (!cacheLifetime) cacheLifetime = {};
    const newValue: any = {};
    const caseList = await this.getCaseList(true);
    const now = new Date().getTime();
    const promises: any = [];
    caseList.forEach((c: any) => {
      // EXP: valueというプロパティがライフタイムになっていて、現在時刻がそれを超えてしまったら削除する（またはlifetime=0なら強制的に削除）
      if (lifetime === 0 || (cacheLifetime[c.dataId] && now > cacheLifetime[c.dataId].value) || c.deleted) {
        promises.push(this.bulkDeleteLocal(c.dataId));
        return;
      }

      // OBJ: 状態がwillArchive,archivedのものは、古いものから削除する。必要ならまた取得すれば良いという考え。
      if (lifetime > 0 && !cacheLifetime[c.dataId]) {
        newValue[c.dataId] = now + lifetime;
        return;
      }
      newValue[c.dataId] = cacheLifetime[c.dataId].value;
    });
    if (promises.length > 0) await Promise.all(promises);
    await this.db.meta.put({name: 'cacheLifetime', value: newValue});

    await super.purge(); // deletedフラグが付いたもの、removeAtが満了したものを消す
  }

  public rawBulkPut = async (records: any) => this.rawPutAdapter(records);
  public archiveForTesting = async () => await this.archiveForTestingAdapter();
  public forceDeleteCaseForTesting = async (caseId: string) => await this.forceDeleteCaseForTestingAdapter(caseId);
}

export type {TArchiveStat};
export {MultiOrgCaseDataManage};
